// Address validation patterns and constants
// Shared between frontend and backend for consistent validation

import { type Country, COUNTRIES } from '../models/address';

export { COUNTRIES, type Country };

export const ADDRESS_VALIDATION = {
  // Required field patterns - basic validation for string fields
  STREET_REQUIRED: true,
  NUMBER_REQUIRED: true,
  CITY_REQUIRED: true,
  ZIP_REQUIRED: true,
  COUNTRY_REQUIRED: true,

  // Optional field patterns
  STATE_OPTIONAL: true,

  // URL name validation for public booking links
  URL_NAME: {
    PATTERN: /^[a-z0-9-]*$/,
    MAX_LENGTH: 50,
    // Transformation rules for user input
    TRANSFORM: {
      LOWERCASE: true,
      REMOVE_INVALID_CHARS: /[^a-z0-9-]/g,
      REPLACE_MULTIPLE_HYPHENS: /--+/g,
      REMOVE_LEADING_TRAILING_HYPHENS: /^-+|-+$/g,
    },
  },
} as const;

// Basic validation functions (no framework dependencies)
export const isValidCountry = (country: string): country is Country => {
  return COUNTRIES.includes(country as Country);
};

export const isValidUrlName = (urlName: string): boolean => {
  if (!urlName) return true; // Optional field
  return (
    urlName.length <= ADDRESS_VALIDATION.URL_NAME.MAX_LENGTH &&
    ADDRESS_VALIDATION.URL_NAME.PATTERN.test(urlName)
  );
};

export const transformUrlName = (input: string): string => {
  const { TRANSFORM } = ADDRESS_VALIDATION.URL_NAME;
  return input
    .toLowerCase()
    .replace(TRANSFORM.REMOVE_INVALID_CHARS, '')
    .replace(TRANSFORM.REPLACE_MULTIPLE_HYPHENS, '-')
    .replace(TRANSFORM.REMOVE_LEADING_TRAILING_HYPHENS, '');
};

// Safe address parsing with country preservation and error handling
export const parseShopAddress = (
  addressField: string | object | null | undefined,
  shopId?: string,
): any => {
  if (!addressField) return {};

  // If already an object, return it directly (no parsing needed)
  if (typeof addressField === 'object') {
    return addressField;
  }

  try {
    const parsed = JSON.parse(addressField);
    if (parsed && typeof parsed === 'object') {
      // Validate that country code wasn't lost during parsing
      const hasCountryInOriginal = addressField.includes('"country"');
      const hasCountryInParsed = parsed.country;

      if (hasCountryInOriginal && !hasCountryInParsed) {
        console.error('CRITICAL: Country code lost during address parsing', {
          shopId,
          originalAddress: addressField,
          parsedAddress: parsed,
          timestamp: new Date().toISOString(),
        });
        // Add monitoring/alerting here in the future
      }

      return parsed;
    }
    return {};
  } catch (error) {
    console.error(`Failed to parse shop address for shop ${shopId}:`, error, {
      originalAddress: addressField,
      timestamp: new Date().toISOString(),
    });
    // Return empty object but log the failure for monitoring
    return {};
  }
};

// --- Postcode validation (country-aware) ---------------------------------------
// DE: 5 digits. GB: standard alphanumeric formats incl. the GIR 0AA edge case.
// Add new countries here; an unmapped country is not blocked (returns valid).
export const POSTCODE_PATTERNS: Partial<Record<Country, RegExp>> = {
  DE: /^\d{5}$/,
  GB: /^(GIR ?0AA|[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i,
};

/**
 * Canonical stored form of a postcode. GB postcodes are uppercased with a single
 * space before the final three chars (`sw1a1aa` -> `SW1A 1AA`); others are trimmed.
 * Normalise at the validator boundary, not at every read site.
 */
export const normalisePostcode = (value: string, country: Country): string => {
  const trimmed = (value ?? '').trim();
  if (country === 'GB') {
    const compact = trimmed.toUpperCase().replace(/\s+/g, '');
    return compact.length > 3
      ? `${compact.slice(0, -3)} ${compact.slice(-3)}`
      : compact;
  }
  return trimmed;
};

export const isValidPostcode = (value: string, country: Country): boolean => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return false;
  const pattern = POSTCODE_PATTERNS[country];
  if (!pattern) return true; // unknown country: don't block sign-up
  return pattern.test(normalisePostcode(trimmed, country));
};

/** Field label appropriate to the address's country. */
export const getPostcodeLabel = (country: Country): string =>
  country === 'GB' ? 'Postcode' : country === 'DE' ? 'PLZ' : 'ZIP';

/**
 * Resolve the first display line of an address. Prefers the new `line1` field;
 * falls back to concatenating the legacy `street` + `number` split for rows
 * written before ADDR-004. Returns '' when neither shape has content so
 * callers can safely interpolate without producing 'undefined undefined'.
 *
 * The parameter is intentionally narrower than `Partial<Address>` — only the
 * four read fields are required — so legacy form types (BusinessInfo etc.)
 * that haven't migrated to the full `Address` type can still call the helper
 * without an extra cast.
 */
export type AddressLine1Input =
  | {
      line1?: string | null;
      line2?: string | null;
      street?: string | null;
      number?: string | null;
    }
  | null
  | undefined;

export const getAddressLine1 = (address: AddressLine1Input): string => {
  if (!address) return '';
  if (address.line1?.trim()) return address.line1.trim();
  const street = address.street?.trim() ?? '';
  const number = address.number?.trim() ?? '';
  return `${street} ${number}`.trim();
};
